// WebUSB driver for a stock GB-Link device. Keeps one read in flight on each IN endpoint
// and reports statuses, 64-byte link data and short (reply/telemetry) frames to listeners.

import {
  Cmd,
  EP_COMMAND,
  EP_DATA,
  FirmwareInfo,
  LINK_FRAME_BYTES,
  ModeId,
  RP2_BOOT_PRODUCT_ID,
  RP2_BOOT_VENDOR_ID,
  Status,
  USB_PRODUCT_ID,
  USB_VENDOR_ID,
} from "./gblink";

export interface GbLinkEvents {
  status(code: number): void;
  linkData(frame: Uint8Array): void;
  shortData(bytes: Uint8Array): void;
  disconnected(reason: string): void;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export class ClaimError extends Error {}

/** The user picked a Pico in boot mode: it needs GB-Link firmware before it can be used. */
export class BootModeError extends Error {}

export class GbLinkDevice {
  private running = false;
  private statusWaiters: Array<{ want: number; resolve: () => void }> = [];
  private replyWaiters: Array<{ first: number; resolve: (b: Uint8Array) => void }> = [];

  private constructor(
    readonly usb: USBDevice,
    private readonly on: GbLinkEvents,
  ) {}

  static isSupported(): boolean {
    return typeof navigator !== "undefined" && "usb" in navigator;
  }

  /** Ask the user to pick a device (must be called from a click handler). */
  static async request(on: GbLinkEvents): Promise<GbLinkDevice> {
    const usb = await navigator.usb.requestDevice({
      filters: [
        { vendorId: USB_VENDOR_ID, productId: USB_PRODUCT_ID },
        { vendorId: RP2_BOOT_VENDOR_ID, productId: RP2_BOOT_PRODUCT_ID },
      ],
    });
    if (usb.vendorId === RP2_BOOT_VENDOR_ID && usb.productId === RP2_BOOT_PRODUCT_ID) {
      throw new BootModeError("This Pico is in boot mode and doesn't have GB-Link firmware yet.");
    }
    return GbLinkDevice.open(usb, on);
  }

  /** Reconnect to a device this origin was already granted, if one is plugged in. */
  static async reopen(on: GbLinkEvents): Promise<GbLinkDevice | null> {
    const devices = await navigator.usb.getDevices();
    const usb = devices.find((d) => d.vendorId === USB_VENDOR_ID && d.productId === USB_PRODUCT_ID);
    return usb ? GbLinkDevice.open(usb, on) : null;
  }

  private static async open(usb: USBDevice, on: GbLinkEvents): Promise<GbLinkDevice> {
    await usb.open();
    if (usb.configuration === null) await usb.selectConfiguration(1);
    try {
      await usb.claimInterface(0);
    } catch {
      await usb.close().catch(() => {});
      throw new ClaimError(
        "Another tab is using the GB-Link. Close any GB-Link launcher or online tabs, then try again.",
      );
    }
    const dev = new GbLinkDevice(usb, on);
    dev.running = true;
    void dev.pumpStatus();
    void dev.pumpData();
    return dev;
  }

  get productName(): string {
    return this.usb.productName ?? "GB-Link";
  }

  async command(...bytes: number[]): Promise<void> {
    await this.usb.transferOut(EP_COMMAND, new Uint8Array(bytes));
  }

  async sendLinkData(frame: Uint8Array<ArrayBuffer>): Promise<void> {
    if (frame.length !== LINK_FRAME_BYTES) throw new Error(`link frames must be ${LINK_FRAME_BYTES} bytes`);
    await this.usb.transferOut(EP_DATA, frame);
  }

  async firmwareInfo(timeoutMs = 1500): Promise<FirmwareInfo> {
    const reply = this.waitReply(Cmd.GetFirmwareInfo, timeoutMs);
    await this.command(Cmd.GetFirmwareInfo);
    const b = await reply;
    if (b.length < 4) throw new Error("short firmware info reply");
    return { major: b[1], minor: b[2], patch: b[3] };
  }

  /**
   * Start a mode the way the device expects: cancel whatever runs, settle, 3.3 V rail,
   * SetMode, then wait for DeviceReady. The mode's entry status (AwaitMode) follows via events.
   */
  async startMode(mode: ModeId, variant?: number): Promise<void> {
    await this.command(Cmd.Cancel);
    await sleep(500);
    await this.command(Cmd.SetVoltage3V3);
    const ready = this.waitStatus(Status.DeviceReady, 2500);
    await this.command(...(variant === undefined ? [Cmd.SetMode, mode] : [Cmd.SetMode, mode, variant]));
    await ready;
  }

  async cancel(): Promise<void> {
    await this.command(Cmd.Cancel);
  }

  async close(): Promise<void> {
    if (!this.running) return;
    this.running = false;
    await this.cancel().catch(() => {});
    await this.usb.releaseInterface(0).catch(() => {});
    await this.usb.close().catch(() => {});
  }

  waitStatus(want: number, timeoutMs: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const w = { want, resolve: () => (clearTimeout(t), resolve()) };
      const t = setTimeout(() => {
        this.statusWaiters = this.statusWaiters.filter((x) => x !== w);
        reject(new Error(`timed out waiting for status 0x${want.toString(16)}`));
      }, timeoutMs);
      this.statusWaiters.push(w);
    });
  }

  private waitReply(first: number, timeoutMs: number): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const w = { first, resolve: (b: Uint8Array) => (clearTimeout(t), resolve(b)) };
      const t = setTimeout(() => {
        this.replyWaiters = this.replyWaiters.filter((x) => x !== w);
        reject(new Error("no reply from device"));
      }, timeoutMs);
      this.replyWaiters.push(w);
    });
  }

  private async pumpStatus(): Promise<void> {
    while (this.running) {
      let r: USBInTransferResult;
      try {
        r = await this.usb.transferIn(EP_COMMAND, 64);
      } catch (e) {
        return this.fail(e);
      }
      if (!r.data) continue;
      const v = r.data;
      for (let i = 0; i + 1 < v.byteLength; i += 2) {
        const code = v.getUint16(i, true);
        const hit = this.statusWaiters.filter((w) => w.want === code);
        this.statusWaiters = this.statusWaiters.filter((w) => w.want !== code);
        hit.forEach((w) => w.resolve());
        this.on.status(code);
      }
    }
  }

  private async pumpData(): Promise<void> {
    while (this.running) {
      let r: USBInTransferResult;
      try {
        r = await this.usb.transferIn(EP_DATA, 64);
      } catch (e) {
        return this.fail(e);
      }
      if (!r.data || r.data.byteLength === 0) continue;
      const bytes = new Uint8Array(r.data.buffer, r.data.byteOffset, r.data.byteLength).slice();
      if (bytes.length === LINK_FRAME_BYTES) {
        this.on.linkData(bytes);
        continue;
      }
      const w = this.replyWaiters.find((x) => x.first === bytes[0]);
      if (w) {
        this.replyWaiters = this.replyWaiters.filter((x) => x !== w);
        w.resolve(bytes);
      }
      this.on.shortData(bytes);
    }
  }

  private fail(e: unknown): void {
    if (!this.running) return;
    this.running = false;
    this.on.disconnected(e instanceof Error ? e.message : String(e));
  }
}
